import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CrudLink, CustomCellDate } from "@/shared/components";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import { PagePaths } from "@/admin/shared/constants";
import type { ContractsListRow } from "@/admin/entities/contracts";
import type { ContractsListQueryType } from "@/types/graphql/admin.generated";
import {
  CancelContractButton,
  CONTRACT_ITEM_TYPE_LABEL,
  contractPeriodAmount,
  formatContractCurrency,
  formatContractPeriod,
  renderContractStatus,
} from "../domains";

export function getTableData(data: unknown): {
  nodes: ContractsListRow[];
  count: number;
} {
  const typed = data as ContractsListQueryType;
  const result = typed?.contracts;
  if (!result?.nodes) return { nodes: [], count: 0 };
  return { nodes: result.nodes, count: result.count };
}

export function mapColumns(contract: ContractsListRow) {
  return [
    contract.id,
    contract,
    contract,
    contract.status,
    contract,
    contract,
    contract,
    contract,
    contract.createdAt,
    contract,
  ];
}

export const getColumnsConfig = (): ColumnDefinition<ContractsListRow>[] => [
  { name: "id", label: "ID", options: { display: false, filter: false } },
  {
    name: "title",
    label: "Название",
    options: {
      customBodyRender: (data: unknown) => {
        const contract = data as ContractsListRow;
        return (
          <CrudLink tab="details" id={contract.id}>
            {contract.title}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "organization",
    label: "Организация",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const contract = data as ContractsListRow;
        return (
          <CrudLink
            path={PagePaths.organizations}
            tab="details"
            id={contract.organizationId}
          >
            {contract.organization?.title ?? ""}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "status",
    label: "Статус",
    options: {
      customBodyRender: (value: unknown) => renderContractStatus(String(value)),
    },
  },
  {
    name: "currency",
    label: "Валюта",
    options: {
      // Валюта счёта видна только когда она отличается: «USD → EUR».
      customBodyRender: (data: unknown) => {
        const contract = data as ContractsListRow;
        return formatContractCurrency(
          contract.currency,
          contract.invoiceCurrency
        );
      },
    },
  },
  {
    name: "period",
    label: "Период",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const contract = data as ContractsListRow;
        return formatContractPeriod(contract.dateFrom, contract.dateTo);
      },
    },
  },
  {
    name: "items",
    label: "Строки",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const contract = data as ContractsListRow;
        return contract.items
          .map((item) => CONTRACT_ITEM_TYPE_LABEL[item.type] ?? item.type)
          .join(", ");
      },
    },
  },
  {
    name: "amount",
    label: "Сумма за период",
    options: {
      sort: false,
      customBodyRender: (data: unknown) =>
        contractPeriodAmount(data as ContractsListRow),
    },
  },
  {
    name: "createdAt",
    label: "Создан",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => (
        <CustomCellDate date={value as string} />
      ),
    },
  },
  // Действия строкой, как в остальных разделах админки (см. покупки
  // продуктов). Уже отменённому контракту кнопка не нужна.
  {
    name: "actions",
    label: "",
    options: {
      sort: false,
      setCellHeaderProps: () => ({ style: { width: 170 } }),
      customBodyRender: (data: unknown) => {
        const contract = data as ContractsListRow;
        if (contract.status === "canceled") return null;
        return <CancelContractButton contractId={contract.id} />;
      },
    },
  },
];

export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Контракты",
  itemCreated: "Контракт создан",
  itemUpdated: "Контракт обновлён",
  createItem: "Создать контракт",
  refetchList: "Обновить список",
  cancel: "Отмена",
  editItem: "Контракт",
  showTitle: "Детали",
});

export const getMobileColumnsConfig =
  (): DataListViewColumns<ContractsListRow> => ({
    main: {
      title: "Контракт",
      sx: { flex: 1, minWidth: 0 },
      renderCell: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography noWrap>{row.title}</Typography>
          <Typography variant="body2" color="textSecondary" noWrap>
            {row.organization?.title ?? ""}
          </Typography>
        </Stack>
      ),
    },
    status: {
      title: "Статус",
      renderCell: (row) => renderContractStatus(row.status),
    },
  });
